// Long-press → synthetic `contextmenu` shim (coarse-pointer only).
//
// iPad Safari fires the native `contextmenu` event inconsistently on a long
// touch and NEVER for Apple Pencil, so every per-item action menu wired through
// `onContextmenu` / `addEventListener('contextmenu')` (files rename/delete/move,
// canvas node ops, ticket ops) is unreachable by touch. This detector watches
// for a ~500ms stationary finger/pen press and, on fire, dispatches a synthetic
// `contextmenu` MouseEvent at the touch point on the REAL touched target.
//
// Because that synthetic event bubbles (`bubbles: true`) and is otherwise
// indistinguishable from a real right-click, it drives BOTH transports already
// present in this repo:
//   • DOMQL `onContextmenu` flat handlers (canvas surface/item)
//   • native `addEventListener('contextmenu', …)` listeners (files/tickets stage)
// and it honours whatever `stopPropagation` those handlers already do (e.g.
// CanvasItem stops the bubble so the item menu wins over the empty-canvas menu).
// That faithfulness is why arming ONCE at a surface's stage/root element covers
// every child — the event bubbles up from the touched descendant exactly as a
// real right-click would.
//
// `this`-bound: `this` = the element. Wire via `el.call('armLongPress')` /
// `this.call('armLongPress')` inside a surface's existing onCreate/onRender,
// beside its contextmenu setup. Idempotent per node.

export function armLongPress() {
  // FA204 / frank trap — inlined as self-contained locals (this file's sole
  // export is a `this`-bound function with no `el`/`root` shared-state
  // argument; these are pure constants, not cross-call state, so they get
  // no window/globalThis re-home — just moved inside the one function that
  // reads them, per project_frank_drops_module_local_helpers).
  const LONG_PRESS_MS = 500 // iOS system long-press threshold
  const MOVE_TOLERANCE = 10 // px of finger travel that cancels the press

  const el = this
  const node = el && el.node
  // Reading el.node + attaching native listeners is the established pattern for
  // contextmenu on these surfaces (DOMQL doesn't delegate `onContextmenu` in the
  // shell). Idempotent per node — a re-render swaps in a fresh unflagged node.
  if (!node || node.__longPressArmed) return

  // Fine-pointer devices already get a real `contextmenu` on right-click, so the
  // shim only attaches on touch surfaces. Gate on the shared capability layer
  // (memoized) — zero listener overhead on desktop.
  if (typeof el.call === 'function' && !el.call('isCoarsePointer')) return
  node.__longPressArmed = true

  // Transient press-tracking state lives in this closure (per-arm, non-reactive):
  // a 500ms timer + the press-start coordinates. It is deliberately NOT on
  // el.scope — that is a reactive signal store, and a rapidly-cleared timer id
  // has no business creating signals or triggering effects.
  let timer = null
  let startX = 0
  let startY = 0

  const clear = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  const onPointerdown = (e) => {
    // Only synthesize for touch / pen. A mouse or trackpad — even on a hybrid
    // coarse device (iPad + Magic Keyboard) — keeps its native right-click menu,
    // so guard against a phantom long-press there too.
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return
    clear()
    startX = e.clientX
    startY = e.clientY
    const target = e.target
    timer = setTimeout(() => {
      timer = null
      if (!target) return
      // Dispatch on the actual touched descendant so the event bubbles through
      // every ancestor handler (item → surface) — clientX/Y at the stationary
      // press point feed the menu's anchor + `target.closest()` item resolution.
      target.dispatchEvent(
        new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX: startX,
          clientY: startY,
          view: window
        })
      )
    }, LONG_PRESS_MS)
  }

  const onPointermove = (e) => {
    if (!timer) return
    // A finger that travels past the tolerance is a scroll/pan/drag, not a press.
    if (
      Math.abs(e.clientX - startX) > MOVE_TOLERANCE ||
      Math.abs(e.clientY - startY) > MOVE_TOLERANCE
    )
      clear()
  }

  // Passive: these handlers never call preventDefault synchronously (the only
  // side effect is a deferred synthetic dispatch), so they can't block scroll
  // and coexist with the surface's own pan/marquee/drag pointer handlers.
  node.addEventListener('pointerdown', onPointerdown, { passive: true })
  node.addEventListener('pointermove', onPointermove, { passive: true })
  node.addEventListener('pointerup', clear, { passive: true })
  node.addEventListener('pointercancel', clear, { passive: true })
}
